import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Calendar, Loader2, Send, PlayCircle, ChevronDown } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/wokelo/wokelo-support-reachout";

// Placeholder tutorial videos — swap these with real Loom/YouTube embeds later.
// Each item: { id, title, duration, thumb (gradient class for now) }
const TUTORIALS = [
  { id: 1, title: "Getting started with Slides by Wokelo", duration: "2:14", thumb: "from-teal-200 to-emerald-300" },
  { id: 2, title: "Generating your first single slide", duration: "3:08", thumb: "from-amber-200 to-orange-300" },
  { id: 3, title: "Building a multi-slide deck end-to-end", duration: "4:42", thumb: "from-violet-200 to-purple-300" },
  { id: 4, title: "Templates & brand customization", duration: "2:55", thumb: "from-sky-200 to-blue-300" },
  { id: 5, title: "Research mode + grounded sources", duration: "3:31", thumb: "from-rose-200 to-pink-300" },
];

const FAQS = [
  {
    q: "How are credits consumed?",
    a: "1 file upload costs 1 credit. Each generated slide costs 5 credits. Regenerating an existing slide also costs 5 credits.",
  },
  {
    q: "What happens to my unused credits?",
    a: "On Starter and Pro plans, unused credits roll over up to 2× your monthly allotment. Top-up credits never expire.",
  },
  {
    q: "Can I cancel my subscription?",
    a: "Yes — anytime. Open Account Details → Billing → Manage subscription, or visit your Stripe billing portal directly.",
  },
  {
    q: "What file types can I upload?",
    a: "Trial users: PDF only (max 10 MB, 50 pages). Paid plans: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, PNG, JPG, JPEG, WEBP.",
  },
  {
    q: "Where do my slides get stored?",
    a: "All generated slides + uploaded files stay private to your account. Workspace teammates can only see content explicitly shared. Everything is encrypted at rest.",
  },
  {
    q: "Do you offer enterprise plans?",
    a: "Yes — Enterprise includes SSO/SAML, SOC-2/ISO compliance, custom branding, and a dedicated security vault. Contact sales for pricing.",
  },
];

export default function HelpPage() {
  const [request, setRequest] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!request.trim()) { toast.error("Please describe what you need help with"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/support/request`, { message: request }, { withCredentials: true });
      toast.success("Got it — our team will reach out within 1 business day.");
      setRequest("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send your request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page-container max-w-5xl mx-auto px-4 sm:px-8 py-8" data-testid="help-page">
      <h1 className="text-2xl font-bold font-['Manrope'] text-gray-900 mb-2">
        Support
      </h1>
      <p className="text-sm text-gray-500 mb-8">
        We're here to help. Drop us a message, schedule a session, or browse our tutorials.
      </p>

      {/* Two-up CTAs */}
      <div className="grid md:grid-cols-2 gap-4 mb-10">
        {/* Request box */}
        <Card className="p-5 border-gray-200" data-testid="help-request-card">
          <h2 className="text-base font-bold text-gray-900 mb-1">Raise a request</h2>
          <p className="text-xs text-gray-500 mb-3">
            Describe your issue. We typically respond within one business day.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <Textarea
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              placeholder="What can we help with?"
              className="min-h-[110px] bg-white"
              data-testid="help-request-input"
            />
            <Button type="submit" disabled={sending || !request.trim()}
              className="bg-gray-900 hover:bg-black w-full sm:w-auto"
              data-testid="help-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Send</>}
            </Button>
          </form>
        </Card>

        {/* Schedule a session */}
        <Card className="p-5 border-gray-200 flex flex-col" data-testid="help-schedule-card">
          <h2 className="text-base font-bold text-gray-900 mb-1">Schedule a session</h2>
          <p className="text-xs text-gray-500 mb-3">
            Hop on a 15-min call with our team. Walk through your use case live.
          </p>
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-100 rounded-lg p-6 text-center">
            <div>
              <Calendar className="w-7 h-7 mx-auto text-teal-600 mb-2" />
              <p className="text-sm text-gray-600 mb-3">Pick a time that works for you.</p>
              <Button asChild className="bg-teal-600 hover:bg-teal-700">
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="help-calendly-btn">
                  Open Calendly
                </a>
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Tutorial videos */}
      <h2 className="text-base font-bold text-gray-900 mb-3">Tutorial videos</h2>
      <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 mb-10 snap-x snap-mandatory" data-testid="help-tutorials-carousel">
        {TUTORIALS.map((v) => (
          <button
            key={v.id}
            onClick={() => toast.info("Tutorial videos coming soon")}
            className="flex-shrink-0 w-72 rounded-lg border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow snap-start text-left"
            data-testid={`tutorial-${v.id}`}
          >
            <div className={`aspect-video bg-gradient-to-br ${v.thumb} flex items-center justify-center relative`}>
              <PlayCircle className="w-10 h-10 text-white/90 drop-shadow-md" />
              <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-[11px] font-semibold">
                {v.duration}
              </span>
            </div>
            <div className="p-3">
              <p className="text-sm font-semibold text-gray-900 line-clamp-2">{v.title}</p>
            </div>
          </button>
        ))}
      </div>

      {/* FAQ */}
      <h2 className="text-base font-bold text-gray-900 mb-3">Frequently asked questions</h2>
      <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white" data-testid="help-faq-list">
        {FAQS.map((f, i) => (
          <details key={i} className="group" data-testid={`faq-${i}`}>
            <summary className="flex items-center justify-between gap-3 p-4 cursor-pointer hover:bg-gray-50/50 list-none">
              <span className="text-sm font-semibold text-gray-900">{f.q}</span>
              <ChevronDown className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180 flex-shrink-0" />
            </summary>
            <div className="px-4 pb-4 -mt-1 text-sm text-gray-600 leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>
    </div>
  );
}
